import React, { useEffect } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { type ProductKeys } from '../../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import { serveFPSGitHubSocialProofBannerExperiment } from '../../../../../common/utils/experiments';
import { OAuthContainer } from '../../../../../common/utils/oauth-container';
import { useBannerEligibleProductsOrder } from '../../../../../controllers/3p-smart-link-usage';
import { useAnalytics } from '../../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';
import { useOAuthHandlers } from '../../../../../controllers/oauth';
import { useSmartLinkUsageAndTopApps } from '../../../../../controllers/store/bootstrap';

export type BulkConnectBannerProps = {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	selectedProduct?: ProductKeys;
	availableProducts: ProductKeys[];
};

// Hide the listitem wrapper when the banner renders nothing to avoid whitespace gaps.
const styles = cssMap({
	hideWhenEmpty: {
		'&:empty': {
			display: 'none',
		},
	},
});

export const BulkConnectBanner = ({
	thirdPartyConfigs,
	selectedProduct,
	availableProducts,
}: BulkConnectBannerProps): React.JSX.Element | null => {
	const { fireAnalyticsEvent } = useAnalytics();
	const smartLinkUsageAndTopApps = useSmartLinkUsageAndTopApps();
	const bannerEligibleProductsOrder = useBannerEligibleProductsOrder({
		isOnSearchDialog: false,
		thirdPartyConfigs,
		availableProducts,
	});

	useEffect(() => {
		serveFPSGitHubSocialProofBannerExperiment(
			{ thirdPartyConfigs },
			{ fireExperimentExposure: true },
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally fire-once on mount; thirdPartyConfigs changes after initial load should not re-fire the exposure
	}, []);

	if (!selectedProduct) {
		return (
			<ErrorBoundary section="SearchPageRelayResultsOAuthContainer">
				<Box role="listitem" xcss={styles.hideWhenEmpty}>
					<OAuthContainer
						thirdPartyConfigs={thirdPartyConfigs}
						eligibleProducts={bannerEligibleProductsOrder}
						useOAuthHandlers={useOAuthHandlers}
						fireAnalyticsEvent={fireAnalyticsEvent}
						teamConnectedPercentage={smartLinkUsageAndTopApps?.percentage}
					/>
				</Box>
			</ErrorBoundary>
		);
	}

	return null;
};
